// Schulferien: Aufruf über Supabase Edge Function `school-holidays` (OpenHolidays, gecacht).
// Fallback: direkt OpenHolidays (Schulferien + gesetzliche Feiertage).

import { getSupabaseClient } from "../supabase/index.js";
import { isGermanTrainingSubdivision } from "./german-training-subdivisions.js";

function asStr(v) {
  return String(v ?? "").trim();
}

function isYmd(s) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(s || "").trim());
}

const OPENHOLIDAYS_BASE = "https://openholidaysapi.org";

/**
 * Soll die Schulferien-API aufgerufen werden?
 * Explizit aus = false; explizit an = true; sonst: gültiges Bundesland → laden (Trainingsregion genügt).
 * @param {object|null|undefined} settings
 * @returns {boolean}
 */
export function inferSchoolHolidaysApiEnabled(settings) {
  const raw = settings?.trainerstundenSchoolHolidaysEnabled;
  if (raw === false || raw === "false" || raw === 0 || raw === "0")
    return false;
  if (raw === true || raw === "true" || raw === 1 || raw === "1") return true;
  const sub = asStr(settings?.trainerstundenTrainingState);
  return isGermanTrainingSubdivision(sub);
}

function pickOpenHolidayName(nameArr) {
  if (!Array.isArray(nameArr) || !nameArr.length) return "";
  const de = nameArr.find(
    (x) => String(x?.language || "").toUpperCase() === "DE",
  );
  return String(de?.text || nameArr[0]?.text || "").trim();
}

function normalizeOpenHolidayRows(raw, idPrefix = "") {
  const out = [];
  if (!Array.isArray(raw)) return out;
  for (const row of raw) {
    const id = asStr(row?.id);
    const startRaw = asStr(row?.startDate);
    const endRaw = asStr(row?.endDate);
    const startDate = /^(\d{4}-\d{2}-\d{2})/.exec(startRaw)?.[1] || "";
    const endDate = /^(\d{4}-\d{2}-\d{2})/.exec(endRaw)?.[1] || "";
    if (!id || !isYmd(startDate) || !isYmd(endDate)) continue;
    out.push({
      id: idPrefix ? `${idPrefix}${id}` : id,
      startDate,
      endDate,
      name: pickOpenHolidayName(row?.name),
    });
  }
  return out;
}

/**
 * Direkt OpenHolidays: Schulferien + gesetzliche Feiertage (z. B. Allerheiligen).
 * @returns {Promise<{ ok: boolean, holidays?: Array, reason?: string }>}
 */
export async function fetchOpenHolidaysSchoolAndPublic({
  subdivisionCode,
  validFrom,
  validTo,
} = {}) {
  const sub = asStr(subdivisionCode);
  const from = asStr(validFrom);
  const to = asStr(validTo);
  if (!isGermanTrainingSubdivision(sub)) {
    return { ok: false, reason: "invalid-subdivision" };
  }
  if (!isYmd(from) || !isYmd(to) || from > to) {
    return { ok: false, reason: "invalid-date-range" };
  }

  const mkUrl = (path) => {
    const url = new URL(`${OPENHOLIDAYS_BASE}${path}`);
    url.searchParams.set("countryIsoCode", "DE");
    url.searchParams.set("subdivisionCode", sub);
    url.searchParams.set("validFrom", from);
    url.searchParams.set("validTo", to);
    url.searchParams.set("languageIsoCode", "DE");
    return url.toString();
  };

  try {
    const [schoolRes, publicRes] = await Promise.all([
      fetch(mkUrl("/SchoolHolidays"), {
        headers: { Accept: "application/json" },
      }),
      fetch(mkUrl("/PublicHolidays"), {
        headers: { Accept: "application/json" },
      }),
    ]);
    if (!schoolRes.ok && !publicRes.ok) {
      return { ok: false, reason: "upstream-error" };
    }
    const schoolRaw = schoolRes.ok ? await schoolRes.json() : [];
    const publicRaw = publicRes.ok ? await publicRes.json() : [];
    const byId = new Map();
    for (const h of [
      ...normalizeOpenHolidayRows(schoolRaw, "school:"),
      ...normalizeOpenHolidayRows(publicRaw, "public:"),
    ]) {
      byId.set(h.id, h);
    }
    const holidays = [...byId.values()].sort((a, b) =>
      a.startDate.localeCompare(b.startDate),
    );
    return { ok: true, holidays };
  } catch (e) {
    return { ok: false, reason: String(e?.message || "request-failed") };
  }
}

/**
 * Lädt Schulferien in Jahres-Slices (jeweils ≤366 Tage), damit die Edge-Function (`range-too-large` >600 Tage) nicht scheitert.
 *
 * @param {{ subdivisionCode: string, yearFrom: number, yearTo: number }} input
 * @returns {Promise<{ ok: boolean, holidays?: Array<{ id: string, startDate: string, endDate: string, name: string }>, reason?: string }>}
 */
export async function fetchSchoolHolidaysForYears({
  subdivisionCode,
  yearFrom,
  yearTo,
} = {}) {
  const sub = asStr(subdivisionCode);
  if (!isGermanTrainingSubdivision(sub)) {
    return { ok: false, reason: "invalid-subdivision" };
  }
  let y0 = Number(yearFrom);
  let y1 = Number(yearTo);
  const cy = new Date().getFullYear();
  if (!Number.isFinite(y0)) y0 = cy;
  if (!Number.isFinite(y1)) y1 = cy;
  if (y0 > y1) {
    const t = y0;
    y0 = y1;
    y1 = t;
  }
  const MAX_YEAR_SPAN = 12;
  if (y1 - y0 > MAX_YEAR_SPAN) {
    y1 = y0 + MAX_YEAR_SPAN;
  }

  const byId = new Map();
  for (let y = y0; y <= y1; y += 1) {
    const res = await fetchSchoolHolidays({
      subdivisionCode: sub,
      validFrom: `${y}-01-01`,
      validTo: `${y}-12-31`,
    });
    if (!res?.ok) {
      return { ok: false, reason: res?.reason || "fetch-failed" };
    }
    for (const h of res.holidays || []) {
      const id = asStr(h?.id);
      if (id) byId.set(id, h);
    }
  }
  return { ok: true, holidays: [...byId.values()] };
}

/**
 * @param {{ subdivisionCode: string, validFrom: string, validTo: string }} input
 * @returns {Promise<{ ok: boolean, holidays?: Array<{ id: string, startDate: string, endDate: string, name: string }>, reason?: string, cached?: boolean }>}
 */
export async function fetchSchoolHolidays({
  subdivisionCode,
  validFrom,
  validTo,
} = {}) {
  const sub = asStr(subdivisionCode);
  const from = asStr(validFrom);
  const to = asStr(validTo);
  if (!isGermanTrainingSubdivision(sub)) {
    return { ok: false, reason: "invalid-subdivision" };
  }
  if (!isYmd(from) || !isYmd(to) || from > to) {
    return { ok: false, reason: "invalid-date-range" };
  }

  const supabase = await getSupabaseClient();
  if (supabase) {
    try {
      const { data, error } = await supabase.functions.invoke(
        "school-holidays",
        {
          body: { subdivisionCode: sub, validFrom: from, validTo: to },
        },
      );
      if (!error && data && data.ok !== false) {
        const edgeHolidays = Array.isArray(data.holidays) ? data.holidays : [];
        const pub = await fetchOpenHolidaysSchoolAndPublic({
          subdivisionCode: sub,
          validFrom: from,
          validTo: to,
        });
        const byId = new Map();
        for (const h of edgeHolidays) {
          const id = asStr(h?.id);
          if (!id) continue;
          const keyed =
            id.startsWith("school:") || id.startsWith("public:")
              ? id
              : `school:${id}`;
          byId.set(keyed, { ...h, id: keyed });
        }
        if (pub.ok) {
          for (const h of pub.holidays || []) {
            const hid = String(h.id || "");
            if (hid.startsWith("public:")) byId.set(hid, h);
            if (!edgeHolidays.length && hid.startsWith("school:")) {
              byId.set(hid, h);
            }
          }
        }
        return {
          ok: true,
          holidays: [...byId.values()],
          cached: !!data.cached,
        };
      }
    } catch (_) {
      /* Fallback unten */
    }
  }

  return fetchOpenHolidaysSchoolAndPublic({
    subdivisionCode: sub,
    validFrom: from,
    validTo: to,
  });
}
